import { useState, useCallback, useRef, useEffect } from 'react';
import type { VisualMapping } from '../../../types/wizardTypes';
import {
  mappingsToTemplate,
  templateToMappings,
  tryPatchBodyTemplate,
} from '../converters/mappingTemplateConverter';

interface UseMappingBuilderOptions {
  watch: (field: string) => unknown;
  setValue: (field: string, value: unknown, options?: Record<string, boolean>) => void;
  isSql?: boolean;
}

const stripToJsonFilter = (template: string): string =>
  template.replace(/\s*\|\s*tojson\b/g, '');

// Returns false when a pure JSON-Jinja template has a syntax error (user is mid-edit).
// Conditional templates ({% %}) always return true since Phase A handles them correctly.
const isTemplateJsonParseReady = (template: string): boolean => {
  if (!template.trim()) return true;
  if (/{%/.test(template)) return true; // conditional template - fallback handles these
  const placeholdered = template.replace(/"?\{\{([^}]*)\}\}"?/g, '"__JINJA_TEST__"');
  try {
    JSON.parse(placeholdered.replace(/,(\s*[\]}])/g, '$1'));
    return true;
  } catch {
    return false;
  }
};

const SQL_VAR_REGEX = /(\{\{\s*)([^\s|}]+)/g;
const getSqlVarNames = (sql: string): string[] =>
  Array.from(sql.matchAll(SQL_VAR_REGEX), m => m[2]);

const renameSqlOccurrences = (sql: string, indices: number[], newName: string): string => {
  const targets = new Set(indices);
  let i = 0;
  return sql.replace(SQL_VAR_REGEX, (match, prefix: string) =>
    targets.has(i++) ? `${prefix}${newName}` : match
  );
};

export function useMappingBuilder({ watch, setValue, isSql = false }: UseMappingBuilderOptions) {
  const [mappings, setMappingsState] = useState<VisualMapping[]>([]);
  const hydrated = useRef(false);
  const suppressSync = useRef(false);
  const lastMappedSnapshot = useRef<{ bodyTemplate: string; queryMapJson: string } | null>(null);
  const baseTemplateRef = useRef<string>('');
  const prevMappingsRef = useRef<VisualMapping[]>([]);
  const originalValuesRef = useRef<Map<string, string>>(new Map());
  const sqlNameByIdRef = useRef<Map<string, string>>(new Map());
  const sqlOccurrencesByIdRef = useRef<Map<string, number[]>>(new Map());
  const watchedBodyTemplate = (watch('bodyTemplate') as string) || '';
  // Query params are managed independently by the step component - don't watch them here

  // Hydrate from bodyTemplate on mount (query params managed separately)
  useEffect(() => {
    if (hydrated.current) return;
    const rawBodyTemplate = (watch('bodyTemplate') as string) || '';
    const bodyTemplate = stripToJsonFilter(rawBodyTemplate);
    if (bodyTemplate !== rawBodyTemplate) {
      setValue('bodyTemplate', bodyTemplate);
    }

    baseTemplateRef.current = bodyTemplate;
    // Always suppress the next sync to prevent the empty initial mappings
    // from overwriting the existing bodyTemplate
    suppressSync.current = true;
    if (bodyTemplate) {
      const hydMappings = templateToMappings(bodyTemplate, undefined);
      if (hydMappings.length > 0) {
        setMappingsState(hydMappings);
        prevMappingsRef.current = hydMappings;
      }
    }
    hydrated.current = true;
  }, []);

  // Sync mappings → bodyTemplate only (query params managed separately)
  useEffect(() => {
    if (suppressSync.current) {
      suppressSync.current = false;
      // Don't update prevMappingsRef here - the caller (hydration or
      // watchedBodyTemplate effect) already set it correctly. Overwriting
      // with the stale render-time `mappings` (still []) causes
      // canPatchTemplate to fail on the next unsuppressed run.
      return;
    }

    let bodyTemplate: string;
    // Merge stored original values into prev mappings for restore-on-delete
    const prev = prevMappingsRef.current.map(m => {
      const orig = originalValuesRef.current.get(m.sourceFieldName);
      return orig !== undefined ? { ...m, originalValue: orig } : m;
    });

    const patchedBodyTemplate = baseTemplateRef.current
      ? tryPatchBodyTemplate(baseTemplateRef.current, prev, mappings)
      : null;

    // Patch the existing template whenever we can so static JSON survives visual edits.
    if (patchedBodyTemplate !== null) {
      bodyTemplate = patchedBodyTemplate;
      baseTemplateRef.current = bodyTemplate;
    } else {
      if (isSql && baseTemplateRef.current) {
        let sql = baseTemplateRef.current;
        const prevById = new Map(prevMappingsRef.current.map(m => [m.id, m]));

        const currentIds = new Set(mappings.map(m => m.id));
        const claimed = new Set<number>();
        for (const [id, owned] of sqlOccurrencesByIdRef.current) {
          if (currentIds.has(id)) owned.forEach(i => claimed.add(i));
          else sqlOccurrencesByIdRef.current.delete(id);
        }

        const normalized = mappings.map(m => {
          const old = prevById.get(m.id);
          if (!old) return m;
          const newName = m.targetPath !== old.targetPath ? m.targetPath : m.sourceFieldName;
          const oldName = sqlNameByIdRef.current.get(m.id) || old.sourceFieldName;
          if (newName && oldName && newName !== oldName) {
            let owned = sqlOccurrencesByIdRef.current.get(m.id);
            if (!owned) {
              owned = getSqlVarNames(sql)
                .map((name, i) => (name === oldName && !claimed.has(i) ? i : -1))
                .filter(i => i !== -1);
              owned.forEach(i => claimed.add(i));
              sqlOccurrencesByIdRef.current.set(m.id, owned);
            }
            sql = renameSqlOccurrences(sql, owned, newName);
          }
          if (oldName) sqlNameByIdRef.current.set(m.id, newName || oldName);
          return { ...m, targetPath: newName, sourceFieldName: newName };
        });

        baseTemplateRef.current = sql;
        prevMappingsRef.current = normalized;
        lastMappedSnapshot.current = { bodyTemplate: sql, queryMapJson: '[]' };
        suppressSync.current = true;
        setMappingsState(normalized);
        setValue('bodyTemplate', sql, { shouldDirty: true });
        return;
      }

      // If we already have a base template but cannot parse/patch it safely,
      // keep the original template to avoid flattening and data loss.
      if (baseTemplateRef.current) {
        prevMappingsRef.current = mappings;
        lastMappedSnapshot.current = {
          bodyTemplate: baseTemplateRef.current,
          queryMapJson: '[]',
        };
        return;
      }
 
      const httpMethod = (watch('httpMethod') as string) || 'POST';
      const result = mappingsToTemplate(mappings, httpMethod);
      bodyTemplate = result.bodyTemplate;
      baseTemplateRef.current = bodyTemplate;
    }

    // Clean up original values for removed mappings
    const currentFieldNames = new Set(mappings.map(m => m.sourceFieldName));
    for (const key of originalValuesRef.current.keys()) {
      if (!currentFieldNames.has(key)) {
        originalValuesRef.current.delete(key);
      }
    }

    prevMappingsRef.current = mappings;
    lastMappedSnapshot.current = {
      bodyTemplate,
      queryMapJson: '[]',
    };
    setValue('bodyTemplate', bodyTemplate, { shouldDirty: true });
  }, [mappings]);

  // Re-sync mappings when bodyTemplate changes externally (e.g. from template editor)
  useEffect(() => {
    if (!hydrated.current) return;
    const sanitizedBodyTemplate = stripToJsonFilter(watchedBodyTemplate);
    if (sanitizedBodyTemplate !== watchedBodyTemplate) {
      setValue('bodyTemplate', sanitizedBodyTemplate);
    }

    const currentSnapshot = {
      bodyTemplate: sanitizedBodyTemplate,
      queryMapJson: '[]',
    };

    if (
      lastMappedSnapshot.current &&
      lastMappedSnapshot.current.bodyTemplate === currentSnapshot.bodyTemplate
    ) {
      return;
    }

    if (!isSql && !isTemplateJsonParseReady(sanitizedBodyTemplate)) return;
    baseTemplateRef.current = sanitizedBodyTemplate;
    suppressSync.current = true;
    const newMappings = templateToMappings(sanitizedBodyTemplate, undefined);
    setMappingsState(newMappings);
    prevMappingsRef.current = newMappings;
    lastMappedSnapshot.current = currentSnapshot;
  }, [watchedBodyTemplate, isSql]);

  const addMapping = useCallback(() => {
    const newMapping: VisualMapping = {
      id: crypto.randomUUID(),
      sourceFieldName: '',
      targetPath: '',
      isQueryParam: false,
      useToJson: false,
      isOptional: false,
    };
    setMappingsState(prev => [...prev, newMapping]);
  }, []);

  const updateMapping = useCallback((id: string, updates: Partial<VisualMapping>) => {
    setMappingsState(prev =>
      prev.map(m => (m.id === id ? { ...m, ...updates } : m))
    );
  }, []);

  const removeMapping = useCallback((id: string) => {
    // Merge original values into prevMappingsRef before removal
    // so tryPatchBodyTemplate can restore them
    prevMappingsRef.current = prevMappingsRef.current.map(m => {
      const orig = originalValuesRef.current.get(m.sourceFieldName);
      return orig !== undefined ? { ...m, originalValue: orig } : m;
    });
    setMappingsState(prev => prev.filter(m => m.id !== id));
  }, []);

  const setMappings = useCallback((newMappings: VisualMapping[]) => {
    setMappingsState(newMappings);
  }, []);

  const setMappingOriginalValue = useCallback((sourceFieldName: string, originalValue: string) => {
    // Store in ref to avoid triggering sync effects
    originalValuesRef.current.set(sourceFieldName, originalValue);
  }, []);

  const getOriginalValue = useCallback((sourceFieldName: string): string | undefined => {
    return originalValuesRef.current.get(sourceFieldName);
  }, []);

  const syncFromTemplate = useCallback((bodyTemplate: string) => {
    const sanitizedBodyTemplate = stripToJsonFilter(bodyTemplate || '');
    if (sanitizedBodyTemplate !== (bodyTemplate || '')) {
      setValue('bodyTemplate', sanitizedBodyTemplate, { shouldDirty: true });
    }
    // Only update base and mappings when JSON is valid (skip this check for raw SQL templates).
    if (!isSql && !isTemplateJsonParseReady(sanitizedBodyTemplate)) return;
    baseTemplateRef.current = sanitizedBodyTemplate;
    suppressSync.current = true;
    const newMappings = templateToMappings(sanitizedBodyTemplate, undefined);
    setMappingsState(newMappings);
    prevMappingsRef.current = newMappings;
    // Stamp the snapshot so the watchedBodyTemplate effect's guard short-circuits
    // on the next render and doesn't trigger a second setMappingsState call with
    // fresh UUIDs - which would cause the mappings→bodyTemplate effect to run
    // unsuppressed and incorrectly reset the form state.
    lastMappedSnapshot.current = { bodyTemplate: sanitizedBodyTemplate, queryMapJson: '[]' };
  }, [isSql]);

  return {
    mappings,
    addMapping,
    updateMapping,
    removeMapping,
    setMappings,
    setMappingOriginalValue,
    getOriginalValue,
    syncFromTemplate,
  };
}
